/**
 * Property Manager for HumanBehavior SDK
 * Handles automatic properties, session properties, and user properties
 */

import { getAutomaticProperties, getInitialProperties, getCurrentPageProperties, AutomaticProperties } from './property-detector';

export interface Properties {
    [key: string]: any;
}

export interface PropertyManagerConfig {
    enableAutomaticProperties?: boolean;
    enableSessionProperties?: boolean;
    enableUserProperties?: boolean;
    propertyDenylist?: string[];
}

export class PropertyManager {
    private config: PropertyManagerConfig;
    private automaticProperties: AutomaticProperties;
    private sessionProperties: Properties = {};
    private userProperties: Properties = {};
    private initialProperties: Properties = {};
    private isInitialized: boolean = false;

    constructor(config: PropertyManagerConfig = {}) {
        this.config = {
            enableAutomaticProperties: true,
            enableSessionProperties: true,
            enableUserProperties: true,
            propertyDenylist: [],
            ...config
        };
        
        this.automaticProperties = getAutomaticProperties();
        this.initialize();
    }

    /**
     * Initialize the property manager
     */
    private initialize(): void {
        if (this.isInitialized) return;
        
        // Capture initial properties once
        this.initialProperties = getInitialProperties();
        
        // Load session properties from sessionStorage
        this.loadSessionProperties();
        
        this.isInitialized = true;
    }

    /**
     * Get all properties for an event
     */
    public getEventProperties(eventProperties: Properties = {}): Properties {
        // Auto/session/user/initial property merges happen on top of the
        // caller-provided eventProperties, but we want USER-provided values
        // to win when there's a key collision (intuitive override semantics).
        // We achieve that by merging into `properties` from the bottom up
        // and re-applying eventProperties last.
        const properties: Properties = {};

        // When `enableAutomaticProperties` is false we short-circuit the
        // entire enrichment chain (auto/session/user/initial) instead of
        // just the auto leg. Pre-0.7 turning this flag off only at construction
        // had no observable effect at call time because session+user props
        // still ballooned the payload.
        if (this.config.enableAutomaticProperties) {
            Object.assign(properties, this.getAutomaticProperties());

            if (this.config.enableSessionProperties) {
                Object.assign(properties, this.sessionProperties);
            }

            if (this.config.enableUserProperties) {
                Object.assign(properties, this.userProperties);
            }

            if (!this.sessionProperties['$initial_properties_captured']) {
                Object.assign(properties, this.initialProperties);
                this.setSessionProperty('$initial_properties_captured', true);
            }
        }

        // User-provided keys win over auto/session/user/initial.
        Object.assign(properties, eventProperties);

        // Apply denylist
        this.applyDenylist(properties);

        return properties;
    }

    /**
     * Get automatic properties
     */
    public getAutomaticProperties(): Properties {
        return {
            ...this.automaticProperties,
            ...getCurrentPageProperties() // Always get fresh page properties
        };
    }

    /**
     * Get automatic properties with GeoIP data merged in
     */
    public getAutomaticPropertiesWithGeoIP(geoIPProperties: Record<string, any> = {}): Properties {
        return {
            ...this.automaticProperties,
            ...getCurrentPageProperties(), // Always get fresh page properties
            ...geoIPProperties
        };
    }

    /**
     * Set a session property
     */
    public setSessionProperty(key: string, value: any): void {
        this.sessionProperties[key] = value;
        this.saveSessionProperties();
    }

    /**
     * Set multiple session properties
     */
    public setSessionProperties(properties: Properties): void {
        Object.assign(this.sessionProperties, properties);
        this.saveSessionProperties();
    }

    /**
     * Get a session property
     */
    public getSessionProperty(key: string): any {
        return this.sessionProperties[key];
    }

    /**
     * Remove a session property
     */
    public removeSessionProperty(key: string): void {
        delete this.sessionProperties[key];
        this.saveSessionProperties();
    }

    /**
     * Set a user property
     */
    public setUserProperty(key: string, value: any): void {
        this.userProperties[key] = value;
    }

    /**
     * Set multiple user properties
     */
    public setUserProperties(properties: Properties): void {
        Object.assign(this.userProperties, properties);
    }

    /**
     * Get a user property
     */
    public getUserProperty(key: string): any {
        return this.userProperties[key];
    }

    /**
     * Get a shallow copy of all user properties.
     */
    public getUserProperties(): Properties {
        return { ...this.userProperties };
    }

    /**
     * Get a shallow copy of all session properties.
     */
    public getSessionProperties(): Properties {
        return { ...this.sessionProperties };
    }

    /**
     * Remove a user property
     */
    public removeUserProperty(key: string): void {
        delete this.userProperties[key];
    }

    /**
     * Set a property only if it hasn't been set before
     */
    public setOnce(key: string, value: any, scope: 'session' | 'user' = 'user'): void {
        if (scope === 'session') {
            if (!(key in this.sessionProperties)) {
                this.setSessionProperty(key, value);
            }
        } else {
            if (!(key in this.userProperties)) {
                this.setUserProperty(key, value);
            }
        }
    }

    /**
     * Clear all session properties
     */
    public clearSessionProperties(): void {
        this.sessionProperties = {};
        this.saveSessionProperties();
    }

    /**
     * Clear all user properties
     */
    public clearUserProperties(): void {
        this.userProperties = {};
    }

    /**
     * Reset all properties
     */
    public reset(): void {
        this.clearSessionProperties();
        this.clearUserProperties();
        this.initialProperties = {};
        this.isInitialized = false;
        this.initialize();
    }

    /**
     * Load session properties from sessionStorage
     */
    private loadSessionProperties(): void {
        if (typeof sessionStorage === 'undefined') return;
        
        try {
            const stored = sessionStorage.getItem('hb_session_properties');
            if (stored) {
                this.sessionProperties = JSON.parse(stored);
            }
        } catch (error) {
            console.warn('Failed to load session properties:', error);
        }
    }

    /**
     * Save session properties to sessionStorage
     */
    private saveSessionProperties(): void {
        if (typeof sessionStorage === 'undefined') return;
        
        try {
            sessionStorage.setItem('hb_session_properties', JSON.stringify(this.sessionProperties));
        } catch (error) {
            console.warn('Failed to save session properties:', error);
        }
    }

    /**
     * Apply property denylist
     */
    private applyDenylist(properties: Properties): void {
        if (!this.config.propertyDenylist || this.config.propertyDenylist.length === 0) {
            return;
        }

        this.config.propertyDenylist.forEach(deniedKey => {
            delete properties[deniedKey];
        });
    }

    /**
     * Update automatic properties (call when page changes)
     */
    public updateAutomaticProperties(): void {
        this.automaticProperties = getAutomaticProperties();
    }

    /**
     * Get all properties for debugging
     */
    public getAllProperties(): {
        automatic: Properties;
        session: Properties;
        user: Properties;
        initial: Properties;
    } {
        return {
            automatic: this.getAutomaticProperties(),
            session: { ...this.sessionProperties },
            user: { ...this.userProperties },
            initial: { ...this.initialProperties }
        };
    }
}
